/**
 * One link entry as the composer stores it. Shared by every component that
 * links (text markers today, icons next), so nothing here knows about text.
 */
export type LinkItem = {
  href?: string;
  /** Author-facing name for identifying the entry in the composer. Never rendered. */
  label?: string;
  target?: string;
  rel?: string;
  title?: string;
  ariaLabel?: string;
  /** `true` = bare download attribute; string = suggested filename. */
  download?: string | boolean;
  hreflang?: string;
  referrerPolicy?: string;
};

/**
 * Runtime guard for entries arriving through componentAttributes (untyped,
 * editor-authored data).
 *
 * Only object shape is checked. Field types are not: a single bad field must
 * cost that field, not the whole entry, so per-field validation belongs to
 * `resolveLinkAttributes` where each value is turned into an attribute.
 */
export function isLinkItem(value: unknown): value is LinkItem {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

/**
 * True for a cleared slot. Clearing a link stores `{}` rather than removing
 * the array element, because marker indexes are 1-based positions: dropping an
 * element would silently repoint every later marker.
 */
export function isEmptyLinkItem(item: unknown): boolean {
  if (!isLinkItem(item)) {
    return true;
  }
  return Object.values(item).every(
    (value) =>
      value === undefined ||
      value === null ||
      (typeof value === 'string' && value.trim() === '')
  );
}

/**
 * The entry a `{n|…}` marker points at, by 1-based position. Tolerant of bad
 * data on every axis — a non-array list, a fractional or out-of-range index,
 * a hole in the array — because none of it may throw during a render.
 */
export function getLinkItemAt(links: unknown, oneBasedIndex: number): LinkItem | undefined {
  if (!Array.isArray(links)) {
    return undefined;
  }
  if (!Number.isInteger(oneBasedIndex) || oneBasedIndex < 1) {
    return undefined;
  }
  const entry = links[oneBasedIndex - 1];
  return isLinkItem(entry) ? entry : undefined;
}
